// services/api.ts
const RENDER_API = 'https://phanh-backend.onrender.com/api';

const getApiBaseUrl = () => {
    // 1. Check if running inside Capacitor native app (iOS/Android)
    //    Always use Render backend for production
    const isNative = typeof (window as any)?.Capacitor !== 'undefined' && (window as any)?.Capacitor?.isNativePlatform?.();
    if (isNative) {
        return RENDER_API;
    }

    // 2. Check if running on marketing domains phanh.app or www.phanh.app
    if (typeof window !== 'undefined' && (window.location.hostname === 'phanh.app' || window.location.hostname === 'www.phanh.app')) {
        return RENDER_API;
    }

    // 3. Dev: localhost (only for actual browser development)
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return 'http://localhost:5000/api';
    }

    // 4. Web production: use Render backend
    return RENDER_API;
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = (overrideBusinessId?: string) => {
  const token = localStorage.getItem('authToken');
  const businessId = overrideBusinessId || localStorage.getItem('currentBusinessId');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (businessId) headers['x-business-id'] = businessId;
  return headers;
};

const getCurrentBusinessId = (): string | null => localStorage.getItem('currentBusinessId');

const db = {
  get: (key: string) => { try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : []; } catch { return []; } },
  set: (key: string, data: any) => { localStorage.setItem(key, JSON.stringify(data)); },
};

class ApiService {
  getBaseUrl(): string { return API_BASE_URL; }
  // ============ AUTH API ============
  async login(credentials: { username: string; password: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier: credentials.username, password: credentials.password }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || (error.errors?.[0]?.msg) || 'Đăng nhập thất bại.');
    }
    return await response.json();
  }

  async register(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const ct = response.headers.get('content-type');
    if (ct && ct.includes('application/json')) {
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Register failed');
      return result;
    }
    throw new Error(`Server Error (${response.status})`);
  }

  async socialLogin(token: string): Promise<any> {
    // 15-second timeout – fetch has no built-in timeout, so on iOS networks
    // this call can silently hang and keep the loading spinner forever.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/social-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        signal: controller.signal,
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error((errBody as any).message || 'Social Auth failed');
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async appleNativeLogin(identityToken: string, fullName?: string): Promise<any> {
    // Sends the Apple identity token directly to our backend for verification
    // via Apple's public keys — no Firebase Web SDK involved.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/apple-native-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identityToken, fullName }),
        signal: controller.signal,
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error((errBody as any).message || 'Apple login failed');
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async getProfile(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch profile');
    return await response.json();
  }

  async updateProfile(data: { name?: string; username?: string; phone?: string; dob?: string; gender?: string; address?: string; avatar?: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update profile');
    }
    return await response.json();
  }

  async deleteAccount(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete account');
    }
    return await response.json();
  }

  async logout(): Promise<void> { /* client-side clear handled by AuthContext */ }

  async getCurrentUser(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() });
      if (!response.ok) return null;
      return await response.json();
    } catch { return null; }
  }

  // ============ BUSINESS API ============
  async getMyBusinesses(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch businesses');
    return await response.json();
  }

  // Alias used by SalonContext
  async getBusinesses(): Promise<any> { return this.getMyBusinesses(); }

  async createBusiness(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to create business');
    return r;
  }

  async updateBusiness(id: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to update business');
    return r;
  }

  async deleteBusiness(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete business');
    return await response.json();
  }

  async joinBusiness(businessId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses/join`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ businessId: businessId.trim() }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || data?.error || `Failed to join business (${response.status})`);
    return data;
  }

  // Alias for backward compat
  async joinBusinessByCode(code: string): Promise<any> { return this.joinBusiness(code); }

  async leaveBusiness(businessId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/businesses/${businessId}/leave`, {
      method: 'POST', headers: getAuthHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || data?.error || 'Failed to leave business');
    return data;
  }

  // Get all members (users) of a business with their user info
  async getBusinessMembers(businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    if (!id) return { success: false, data: [] };
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members`, { headers: getAuthHeaders() });
    return await response.json().catch(() => ({ success: false, data: [] }));
  }

  // Owner/Manager: update a member's role
  async updateMemberRole(userId: string, role: string, businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members/${userId}/role`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ role }),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to update role');
    return r;
  }

  // Owner: remove a member
  async removeMember(userId: string, businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members/${userId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to remove member');
    return r;
  }

  // Owner: add a member directly
  async addMember(userId: string, role: string, businessId?: string): Promise<any> {
    const id = businessId || getCurrentBusinessId();
    const response = await fetch(`${API_BASE_URL}/businesses/${id}/members`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ userId, role }),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to add member');
    return r;
  }

  // ============ DASHBOARD API ============
  async getDashboardStats(params?: { date?: string }): Promise<any> {
    try {
      let url = `${API_BASE_URL}/dashboard`;
      if (params?.date) {
        url += `?date=${encodeURIComponent(params.date)}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed');
      return await response.json();
    } catch (e) {
      return { data: { overview: {}, employeeStats: {}, bookingStats: {} } };
    }
  }

  // Health check
  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, { headers: getAuthHeaders() });
      return response.ok;
    } catch { return false; }
  }

  // ============ EMPLOYEE API ============
  async getEmployees(params?: any): Promise<any> {
    try {
      const query = params ? new URLSearchParams(params).toString() : '';
      const response = await fetch(`${API_BASE_URL}/employees${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch employees');
      return await response.json();
    } catch { return { success: false, data: [] }; }
  }

  async getEmployeeById(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch employee');
    return await response.json();
  }

  async createEmployee(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to create employee');
    return r;
  }

  async updateEmployee(id: number | string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to update employee');
    return r;
  }

  async deleteEmployee(id: number | string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/employees/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to delete employee');
    return r;
  }

  async getEmployeeStats(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/employees/stats`, { headers: getAuthHeaders() });
      if (response.ok) return await response.json();
    } catch { }
    return { data: null };
  }

  async changeEmployeeStatus(id: number | string, status: string): Promise<any> {
    return this.updateEmployee(id, { status });
  }

  async searchEmployees(query: string): Promise<any> {
    return this.getEmployees({ search: query });
  }

  // ============ BOOKING API ============
  async getBookings(filters?: any): Promise<any> {
    const query = filters ? new URLSearchParams(filters).toString() : '';
    const response = await fetch(`${API_BASE_URL}/bookings${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch bookings');
    return await response.json();
  }

  async createBooking(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/bookings`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.message || result?.error || `Server error ${response.status}`);
    return result;
  }

  async updateBooking(id: number | string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/bookings/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update booking');
    return await response.json();
  }

  async deleteBooking(id: number | string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/bookings/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete booking');
    return await response.json();
  }

  async getTodayBookings(): Promise<any> {
    try {
      const today = new Date().toLocaleDateString('en-CA');
      const response = await fetch(`${API_BASE_URL}/bookings?date=${today}`, { headers: getAuthHeaders() });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch { return { data: [] }; }
  }

  // ============ SERVICE API ============
  async getServices(businessId?: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/services`, { headers: getAuthHeaders(businessId) });
      if (!response.ok) throw new Error('Failed');
      return await response.json();
    } catch { return { data: [] }; }
  }

  async createService(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/services`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const r = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(r?.message || 'Failed to create service');
    return r;
  }

  async updateService(id: string | number, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update service');
    return await response.json();
  }

  async deleteService(id: string | number): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/services/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete service');
    return await response.json();
  }

  // ============ NOTIFICATION API ============
  async registerFCMToken(token: string, device: string, platform: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/token`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ token, device, platform }),
    });
    if (!response.ok) return null;
    return await response.json();
  }

  async unregisterFCMToken(token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/token`, {
      method: 'DELETE', headers: getAuthHeaders(), body: JSON.stringify({ token }),
    });
    if (!response.ok) return null;
    return await response.json();
  }

  async getNotifications(limit: number = 50, unreadOnly: boolean = false): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/notifications?limit=${limit}&unreadOnly=${unreadOnly}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch notifications');
      return await response.json();
    } catch { return { success: false, data: [] }; }
  }

  async markNotificationAsRead(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
      method: 'PUT', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to mark as read');
    return await response.json();
  }

  async markAllNotificationsAsRead(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
      method: 'PUT', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to mark all as read');
    return await response.json();
  }

  async getNotificationPreferences(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/notifications/preferences`, { headers: getAuthHeaders() });
      if (!response.ok) return null;
      return await response.json();
    } catch { return null; }
  }

  async updateNotificationPreferences(prefs: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/notifications/preferences`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(prefs),
    });
    if (!response.ok) throw new Error('Failed to update preferences');
    return await response.json();
  }

  // ============ SCHEDULE API ============
  async generateBookingCode(businessId: string): Promise<any> {
    // Trigger pre-save hook by doing a no-op update which generates the code
    const response = await fetch(`${API_BASE_URL}/public/migrate-codes`, {
      method: 'POST',
      headers: getAuthHeaders(businessId),
    });
    return await response.json().catch(() => ({}));
  }

  async getSchedules(params?: any): Promise<any> {
    const query = params ? new URLSearchParams(params).toString() : '';
    const response = await fetch(`${API_BASE_URL}/schedules${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch schedules');
    return await response.json();
  }

  async createOrUpdateSchedule(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/schedules`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to save schedule');
    return await response.json();
  }

  async deleteSchedule(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/schedules/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete schedule');
    return await response.json();
  }

  // ============ CUSTOMER API ============
  async getCustomers(params?: any): Promise<any> {
    try {
      const query = params ? new URLSearchParams(params).toString() : '';
      const response = await fetch(`${API_BASE_URL}/customers${query ? '?' + query : ''}`, { headers: getAuthHeaders() });
      if (response.ok) return await response.json();
    } catch { }
    return { success: false, data: [] };
  }

  async getCustomerById(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch customer');
    return await response.json();
  }

  async createCustomer(data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create customer');
    return await response.json();
  }

  async updateCustomer(id: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update customer');
    return await response.json();
  }

  async deleteCustomer(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete customer');
    return await response.json();
  }

  // ============ ONLINE BOOKING SETTINGS ============
  async getOnlineBookingSettings(): Promise<any> {
    try {
      const businessId = getCurrentBusinessId();
      if (!businessId) return { data: null };
      const response = await fetch(`${API_BASE_URL}/businesses/${businessId}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch business');
      const result = await response.json();
      const business = result.data || result;
      const bookingPage = business?.settings?.bookingPage;
      if (bookingPage) {
        return { data: bookingPage };
      }
      // Return default settings if none exist
      return {
        data: {
          title: business?.name || 'Online Booking',
          description: 'Treat yourself to a premium experience. Book your appointment in seconds.',
          fields: {
            customerName: { label: 'Full Name', placeholder: 'e.g. John Doe' },
            phone: { label: 'Phone Number', placeholder: '+84...' },
            email: { label: 'Email Address', placeholder: 'john@example.com' },
            service: { label: 'Select Services', placeholder: 'Choose a service' },
            date: { label: 'Preferred Date', placeholder: '' },
            time: { label: 'Preferred Time', placeholder: '' },
            staff: { label: 'Preferred Professional (Optional)', placeholder: 'Any available professional' },
            referrer: { label: 'Referrer (Optional)', placeholder: 'e.g. Friend, Facebook...', visible: false },
            notes: { label: 'Special Requests', placeholder: 'Tell us about any specific needs...' }
          },
          footerText: ''
        }
      };
    } catch (e) {
      console.error('Failed to load booking settings:', e);
      return { data: null };
    }
  }

  async updateOnlineBookingSettings(settings: any): Promise<any> {
    const businessId = getCurrentBusinessId();
    if (!businessId) throw new Error('No business selected');
    // First get current business to preserve other settings
    const bizResponse = await fetch(`${API_BASE_URL}/businesses/${businessId}`, { headers: getAuthHeaders() });
    if (!bizResponse.ok) throw new Error('Failed to fetch business');
    const bizResult = await bizResponse.json();
    const currentBusiness = bizResult.data || bizResult;
    const updatedSettings = {
      ...currentBusiness.settings,
      bookingPage: settings
    };
    const response = await fetch(`${API_BASE_URL}/businesses/${businessId}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ settings: updatedSettings }),
    });
    if (!response.ok) throw new Error('Failed to update booking settings');
    return await response.json();
  }

  // ============ PUBLIC BOOKING API (No Auth) ============
  async getPublicBookingPage(bookingCode: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/public/book/${bookingCode}`, { headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to load booking page');
    }
    return await response.json();
  }

  async createPublicBooking(bookingCode: string, data: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/public/book/${bookingCode}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to create booking');
    }
    return await response.json();
  }

  // ============ UPLOAD API (Cloudinary) ============
  async uploadAvatar(file: File): Promise<{ url: string; publicId: string }> {
    const token = localStorage.getItem('authToken');
    const businessId = localStorage.getItem('currentBusinessId');
    const formData = new FormData();
    formData.append('image', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (businessId) headers['x-business-id'] = businessId;
    const response = await fetch(`${API_BASE_URL}/upload/avatar`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Avatar upload failed');
    }
    return await response.json();
  }

  async uploadBusinessLogo(file: File): Promise<{ url: string; publicId: string }> {
    const token = localStorage.getItem('authToken');
    const businessId = localStorage.getItem('currentBusinessId');
    const formData = new FormData();
    formData.append('image', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (businessId) headers['x-business-id'] = businessId;
    const response = await fetch(`${API_BASE_URL}/upload/logo`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Logo upload failed');
    }
    return await response.json();
  }

  async uploadBanner(file: File): Promise<{ url: string; publicId: string }> {
    const token = localStorage.getItem('authToken');
    const businessId = localStorage.getItem('currentBusinessId');
    const formData = new FormData();
    formData.append('image', file);
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (businessId) headers['x-business-id'] = businessId;
    const response = await fetch(`${API_BASE_URL}/upload/banner`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error((error as any).message || 'Banner upload failed');
    }
    return await response.json();
  }

  async uploadIllustrations(files: File[]): Promise<{ urls: string[] }> {
    const token = localStorage.getItem('authToken');
    const businessId = localStorage.getItem('currentBusinessId');
    const formData = new FormData();
    files.forEach(f => formData.append('images', f));
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (businessId) headers['x-business-id'] = businessId;
    const response = await fetch(`${API_BASE_URL}/upload/illustrations`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error((error as any).message || 'Illustrations upload failed');
    }
    return await response.json();
  }

  async uploadBookingImages(files: FileList | File[]): Promise<{ success: boolean; imageUrls: string[] }> {

    const formData = new FormData();
    // 'images' matches the multer field name
    Array.from(files).forEach(file => formData.append('images', file));
    
    const response = await fetch(`${API_BASE_URL}/upload/booking-images`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Booking images upload failed');
    }
    return await response.json();
  }

  // ============ REVIEWS API ============
  async getReviews(params?: { date?: string; rating?: string; staffId?: string }): Promise<any> {
    const businessId = getCurrentBusinessId();
    const query = params ? new URLSearchParams(params as any).toString() : '';
    const response = await fetch(
      `${API_BASE_URL}/reviews${query ? '?' + query : ''}`,
      { headers: getAuthHeaders(businessId || '') }
    );
    if (!response.ok) throw new Error('Failed to fetch reviews');
    return await response.json();
  }

  // ============ EARNINGS API ============
  async getEarningsStats(businessId: string, startDate?: string, endDate?: string): Promise<any> {
    let url = `${API_BASE_URL}/earnings/${businessId}`;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch earnings');
    return await response.json();
  }

  /** Fetch completed bookings in a range for chart data (owner only) */
  async getBookingsForChart(startDate: string, endDate: string): Promise<any> {
    let url = `${API_BASE_URL}/bookings?status=completed&startDate=${startDate}&endDate=${endDate}`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch bookings for chart');
    return await response.json();
  }

  // ==========================================
  // ---------- ADMIN ENDPOINTS -------------
  // ==========================================
  async getAdminUsers(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/users`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to fetch users');
    }
    return response.json();
  }

  async toggleUserBlock(userId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/toggle-block`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to toggle block status');
    }
    return response.json();
  }

  async resetUserPassword(userId: string, newPassword: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ newPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to reset password');
    }
    return response.json();
  }

  async deleteUser(userId: string, adminActionPassword: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminActionPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete user');
    }
    return response.json();
  }

  async updateUserPlan(userId: string, plan: 'basic' | 'pro'): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/plan`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ plan })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update user plan');
    }
    return response.json();
  }

  async getAdminBusinesses(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/businesses`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to fetch businesses');
    }
    return response.json();
  }

  async toggleBusinessBlock(businessId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/businesses/${businessId}/toggle-block`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to toggle business status');
    }
    return response.json();
  }

  async adminDeleteBusiness(businessId: string, adminActionPassword: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/businesses/${businessId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminActionPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete business');
    }
    return response.json();
  }

  async sendGlobalNotification(title: string, message: string, targetBusinessId?: string, targetRole?: string, adminActionPassword?: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/notify`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, message, targetBusinessId, targetRole, adminActionPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to send notification');
    }
    return response.json();
  }

  async updateBusinessSubscription(businessId: string, expiryDate: string | null): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/businesses/${businessId}/subscription`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ expiryDate })
    });
    if (!response.ok) throw new Error('Failed to update subscription');
    return response.json();
  }
  // ==========================================
  // MESSAGING API
  // ==========================================
  async getSupportInfo(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/messages/support-info`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to fetch support info');
    }
    return response.json();
  }

  async sendMessage(content: string, receiverId?: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/messages/send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content, receiverId })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to send message');
    }
    return response.json();
  }

  async getConversation(otherUserId?: string): Promise<any> {
    const url = otherUserId ? `${API_BASE_URL}/messages/conversation/${otherUserId}` : `${API_BASE_URL}/messages/conversation`;
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to fetch conversation');
    }
    return response.json();
  }

  async markMessagesAsRead(senderId?: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/messages/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ senderId })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to mark messages as read');
    }
    return response.json();
  }

  async getAdminConversations(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/messages/admin/conversations`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to fetch admin conversations');
    }
    return response.json();
  }

  // ── Business Requests (admin history) ────────────────────────────────────
  async getAdminBusinessRequests(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/business-requests`, { headers: getAuthHeaders() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to fetch business requests');
    }
    return response.json();
  }

  async deleteAdminBusinessRequest(requestId: string, adminActionPassword: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/admin/business-requests/${requestId}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminActionPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete request');
    }
    return response.json();
  }

  async clearAdminBusinessRequests(adminActionPassword: string, deleteAll = false): Promise<any> {
    const url = `${API_BASE_URL}/admin/business-requests${deleteAll ? '?status=all' : ''}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminActionPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to clear requests');
    }
    return response.json();
  }

  async adminSendVerifyCode(requestId: string, adminActionPassword: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/business-requests/${requestId}/send-code`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminActionPassword })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to send verify code');
    }
    return response.json();
  }

  // ============ AI CHAT API ============

  /** Chat với AI trợ lý quản trị (yêu cầu đăng nhập + business context) */
  async chatWithAdminAI(message: string, history: { role: string; content: string }[] = []): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/ai/chat`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ message, history })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'AI request failed');
    }
    return response.json();
  }

  /** Chat với AI trợ lý công cộng (không cần đăng nhập, chỉ dùng bookingCode) */
  async chatWithPublicAI(code: string, message: string, history: { role: string; content: string }[] = []): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/public/ai/chat/${code}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, history })
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Public AI request failed');
    }
    return response.json();
  }
}

export const apiService = new ApiService();
